import { useEffect, useRef, useState, useCallback } from 'react'
import { useAnalytics } from '../../../../common/analytics'
import useOnViewport from 'src/common/hooks/useOnViewport'
import { checkIcon } from './index.module.scss'
import {
  estimatedPaymentsReferenceTable,
  paymentsReferenceTable as deafultPaymentValues,
} from '../SliderCTA/data'
import { numberFormat } from 'src/common/utils'
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useNavigateToApply } from 'src/common/hooks/useNavigateToApply'
import Img from 'src/components/shared/Img'
import Modal from 'src/components/shared/Modal'
import { cfosDisclousures } from 'src/components/layouts/consts'
import { cn } from 'src/utils/cn'
import { Section } from 'src/components/shared/SectionV2'

const amountFormatOptions = {
  style: 'currency',
  currency: 'USD',
}

function getTimeFormat(monthCount) {
  monthCount = Number(monthCount)
  function getPlural(number, word) {
    return (number === 1 && word.one) || word.other
  }

  var months = { one: 'mos', other: 'mos' },
    years = { one: 'year', other: 'yrs' },
    m = monthCount % 12,
    y = Math.floor(monthCount / 12),
    result = []
  if (m < 1) {
    return `${y} years`
  } else {
    y && result.push(y + ' ' + getPlural(y, years))
    m && result.push(m + ' ' + getPlural(m, months))
  }

  return result.join(' ')
}

const DebtReliefCalculator = ({
  data,
  initialAmount = 25000,
  cfosData,
  isCfosDisclousuresVariationActive,
}) => {
  const { track } = useAnalytics()
  const { showWarningSection = true } = data
  const wasInViewport = useRef(false)
  const [isOnViewport] = useOnViewport('debt-assessment', true)

  const useCfosData = cfosData && isCfosDisclousuresVariationActive
  const paymentsReferenceTable = useCfosData ? cfosData : deafultPaymentValues
  const defaultValue =
    paymentsReferenceTable[initialAmount] &&
    paymentsReferenceTable[initialAmount][0]
      ? initialAmount
      : 25000
  const [selectedAmount, setSelectedAmount] = useState(defaultValue)
  const values = Object.keys(paymentsReferenceTable)
  const onViewH2Id = 'debt-relief-calculator'
  const { navigateToApply, getUrl } = useNavigateToApply({
    estimated_debt: selectedAmount,
  })

  const [showModal, setShowModal] = useState(false)

  const handleModalOpen = useCallback(() => {
    setShowModal(true)
    document.body.style.overflow = 'hidden'
  }, [setShowModal])

  const handleModalClose = useCallback(() => {
    setShowModal(false)
    document.body.style.overflow = 'unset'
  }, [setShowModal])

  useEffect(() => {
    if (!wasInViewport.current && isOnViewport) {
      track(
        {
          additional_info: { section: 'data-visualization' },
          track_event: 'section_view',
          event_type: 'track',
        },
        {},
        'section_view'
      )
      wasInViewport.current = true
    }
  }, [track, isOnViewport])

  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        { additional_info: { section: onViewH2Id }, event_type: 'track' },
        {},
        'section_view'
      )
    },
    freezeOnceVisible: true,
  })

  const handleClick = (e, params = {}) => {
    e.preventDefault()
    const variationText = e.target.innerText
    const data = {
      nav_link_section: 'Debt relief calc',
      page_section: 'You may free up $ on your monthly payment',
      click_type: 'Button Click',
      click_id: variationText,
      click_text: `FDR-Web | ${variationText}`,
      form_ss_amount: +selectedAmount,
      track_event: 'button_click',
      ...params,
    }

    track(
      data,
      {
        event_type: 'conversion',
      },
      'button_click'
    )

    navigateToApply()
  }

  const getLinePorcent = (index, limitIndex) => {
    const rowValues = paymentsReferenceTable[selectedAmount]

    return Math.trunc((rowValues[index] * 100) / rowValues[limitIndex])
  }

  const getDetailItem = (info) => (
    <div className="w-full p-4">
      <div
        className="pb-3 text-left text-heading-4 font-bold"
        data-testid="debt-relief-calculator-info-title"
      >
        {info.title}
      </div>
      {info.lines.map((line, index) => (
        <div
          className="my-2 flex items-center  justify-between"
          key={`line-detail-${index}`}
          data-testid={`debt-relief-calculator-v3-info-item-line-${index}`}
        >
          <div
            className={`flex items-center rounded-md px-1 ${line.className} py-4 text-sm`}
            style={{
              ...(line.indexRefernece
                ? {
                    background: `
                linear-gradient(
                  to right,
                  ${line.bgColor} 0%,
                  ${line.bgColor} ${getLinePorcent(
                      line.val,
                      line.indexRefernece
                    )}%,
                  transparent ${
                    100 - getLinePorcent(line.val, line.indexRefernece)
                  }%,
                  transparent 100%
                )
              `,
                  }
                : {}),
            }}
            data-testid={`debt-relief-calculator-v3-info-item-text-${index}`}
          >
            {line.text}{' '}
            {line.iconCheck && <i className={`${checkIcon} ml-2`} />}
          </div>
          <span
            className="text-right text-sm	font-bold"
            data-testid={`debt-relief-calculator-v3-info-item-value-${index}`}
          >
            <span className={cn({ 'text-[#048155]': index === 0 })}>
              {info.useStaticValues
                ? useCfosData
                  ? getTimeFormat(
                      paymentsReferenceTable[selectedAmount][line.indexVal]
                    )
                  : line.val
                : numberFormat(
                    paymentsReferenceTable[selectedAmount][line.val],
                    amountFormatOptions
                  )}
            </span>
            <span className={cn({ 'text-[#048155]': index === 0 })}>
              {line.unit}
            </span>
            <p>{line?.extra}</p>
          </span>
        </div>
      ))}
    </div>
  )

  const value = numberFormat(
    paymentsReferenceTable[selectedAmount][0] -
      paymentsReferenceTable[selectedAmount][2],
    amountFormatOptions
  )

  const ctaLabel = data.buttonText || `See if you save ${value}/mo`

  return (
    <>
      <div id="debt-assessment" />
      <Section
        variant="secondary"
        // className="flex flex-col flex-wrap bg-gray-75 p-4 lg:flex-row lg:px-28 lg:py-10"
        id="data-visualization-debt-relief-calculator"
        data-eh-observe="debt-relief-calculator-v3"
        data-testid="debt-relief-calculator"
      >
        <div
          className="rounded-lg bg-white px-4 py-5 lg:px-10 lg:py-8"
          data-testid="debt-relief-calculator-v3-container"
        >
          <Section.Typography
            variant="heading-large"
            as="h2"
            className="lg:w-160 mb-6 w-full lg:mb-8"
            data-testid="debt-relief-calculator-v3-title"
          >
            You could free up&nbsp;
            <Section.Typography
              as="span"
              variant="heading-large"
              className="text-blue-140"
              data-testid="debt-relief-calculator-v3-amount"
            >
              {numberFormat(
                paymentsReferenceTable[selectedAmount][0] -
                  paymentsReferenceTable[selectedAmount][2],
                amountFormatOptions
              )}
            </Section.Typography>
            &nbsp;on your monthly payment
          </Section.Typography>
          <Section.Typography
            variant="body-medium"
            className="mt-4"
            data-testid="debt-relief-calculator-v3-subtitle"
          >
            {data.subTitle}
          </Section.Typography>
          <form
            className="mx-auto mt-4 max-w-sm"
            data-testid="debt-relief-calculator-v3-form"
          >
            <select
              style={{
                WebkitAppearance: 'none',
                backgroundImage: 'url("/next-assets/icons/chevron-down.svg")',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '24px 24px',
                backgroundPosition: 'calc(100% - 8px) center',
              }}
              id="amount"
              className="text-md block w-full rounded-lg border border-gray-145 p-2.5 font-medium text-gray-555"
              value={selectedAmount}
              onChange={(e) => setSelectedAmount(e.target.value)}
            >
              {values.map((val) => (
                <option
                  key={`vail-item-${val}`}
                  className="text-blue-700"
                  value={val}
                >
                  {numberFormat(val, amountFormatOptions)}
                </option>
              ))}
            </select>
          </form>
          <div
            className="mt-8 flex w-full flex-col items-center justify-around md:flex-row"
            data-testid="debt-relief-calculator-v3-info-items-container"
          >
            {data.items.map((info, index) => (
              <div
                className="w-full"
                key={`info-item-${index}`}
                data-testid={`debt-relief-calculator-v3-info-item-${index}`}
              >
                {getDetailItem(info)}
              </div>
            ))}
          </div>

          <div className="mb-4 flex w-full justify-center text-center lg:mt-8">
            <Section.CTAButton
              href={getUrl()}
              onClick={handleClick}
              label={ctaLabel}
              data-testid="debt-relief-calculator-v3-cta-button"
            />
          </div>

          {isCfosDisclousuresVariationActive ? (
            <div
              className="w-full text-center"
              data-testid="debt-relief-calculator-v3-disclosures"
            >
              <a onClick={handleModalOpen}>
                <Section.Typography
                  variant="body-link-medium"
                  data-testid="debt-relief-calculator-v3-disclosures-link"
                >
                  How are these numbers calculated?
                </Section.Typography>
              </a>
              {showModal ? (
                <Modal
                  bodyClassName="mb-4 mt-9 "
                  onClose={handleModalClose}
                  open={showModal}
                  className="w-100 max-w-[450px] lg:w-[30%]"
                  hasDistanceFromTop={true}
                >
                  <div
                    className="p-5"
                    data-testid="debt-relief-calculator-v3-modal-content"
                  >
                    <span
                      className="text-heading-2 font-bold"
                      data-testid="debt-relief-calculator-v3-modal-title"
                    >
                      How are these numbers calculated?
                    </span>
                    <p
                      className="mt-5 whitespace-pre-line text-left text-14 text-gray-500"
                      data-testid="debt-relief-calculator-v3-modal-disclosures"
                    >
                      {cfosDisclousures}
                    </p>
                    <div className="flex justify-end">
                      <a
                        onClick={handleModalClose}
                        className="group flex w-full items-center justify-center gap-x-3 rounded-lg bg-blue-560 p-3 lg:w-fit"
                      >
                        <span
                          className="whitespace-nowrap text-lg font-bold text-white"
                          data-testid="debt-relief-calculator-v3-modal-back-button"
                        >
                          Go back
                        </span>
                      </a>
                    </div>
                  </div>
                </Modal>
              ) : null}
            </div>
          ) : null}
        </div>

        {showWarningSection && (
          <div className="w-full border-l-4 border-red-500 px-4">
            <h3 className="flex items-center font-bold text-red-500">
              <Img
                src="/next-assets/icons/clock-sand.svg"
                width={16}
                height={16}
                layout="intrinsic"
                className={'size-[16px]'}
                priority
              />
              <span className="ml-2">Don&apos;t wait!</span>
            </h3>
            <p className="py-4">
              If you wait 3 months, you are estimated to pay{' '}
              <span className="font-bold">
                {numberFormat(
                  estimatedPaymentsReferenceTable[selectedAmount][0],
                  amountFormatOptions
                )}{' '}
                more
              </span>{' '}
              in total debt, and{' '}
              <span className="font-bold">
                {numberFormat(
                  estimatedPaymentsReferenceTable[selectedAmount][1],
                  amountFormatOptions
                )}{' '}
                more each month
              </span>{' '}
              to get rid of your debt.
            </p>
            <a
              href={`https://www.freedomdebtrelief.com/home/?from=fdr&estimated_debt=${selectedAmount}`}
              className="cursor-pointer"
            >
              Explore debt solutions
            </a>
          </div>
        )}
      </Section>
    </>
  )
}

export default DebtReliefCalculator
